import dayjs from 'dayjs';
import { concatObjArray, deepCopy, isArray, isDef, isNotEmpty, isNotEmptyAny, isUndef, parseObjectArrayToString, replaceEmpty } from '@lhb/func';
import { fixNumber } from '@/common/utils/ways';
import { PASSENGER_FLOW_WEEKEND, PASSENGER_FLOW_MODE, PASSENGER_FLOW_SPOTS, PASSENGER_FLOW_WEEKDAY } from './components/PassengerFlow/config';
import { getLeaseTermDuration } from './components/LeaseTerm/utils';
import { isShowDiscount, isShowRent } from './components/BusinessTerms/config';

/** 单选/多选限制条件使用'其他' */
export const RESTRICTION_WITH_OTHER = 'withOther';
/** “其他”选项的id（ -1是服务端约定的，传入-1的id表示其他选项） */
export const OTHER_TYPE_ID = -1;
/** “其他”选项的name */
export const OTHER_TYPE_NAME = '其他';
/** “其他”选项的input值(不可与identification有重复，所以写的又下划线又驼峰)（和 console-pc 统一） */
export const OTHER_TYPE_INPUT_TEXT = '_otherTypeInputText_no_repeat';

/**
 * 单选组件的赋值
 * @description 单选组件的赋值，将选中的值转换为 JSON 字符串
 * @param {any} com 组件实例
 * @param {any} value 选中的值
 * @return {*} { selectedId: number, name: string, input?: string }
 * @demo
 */
const singleValueToJSON = (com: any, value: any) => {
  const { propertyConfigOptionVOList: options } = com;
  if (!(isArray(options) && options.length)) return null;
  // 查找对应的选项
  const targetOption = options.find((item) => item.id === value);
  // 其他选项
  if (value === OTHER_TYPE_ID) {
    const inputValue = com[OTHER_TYPE_INPUT_TEXT];
    delete com[OTHER_TYPE_INPUT_TEXT];
    return JSON.stringify({
      input: inputValue,
      selectedId: -1,
      name: OTHER_TYPE_NAME
    });
  }
  if (targetOption) {
    // 和接口约定的格式
    return JSON.stringify({
      selectedId: targetOption.id,
      name: targetOption.name
    });
  }
  return null;
};

/**
 * @description 多选组件的赋值，将选中的值转换为 JSON 字符串
 * @param {any} com 组件实例
 * @param {number} value 选中的值
 * @return {*} { selectedId: number, name: string, input?: string }[]
 * @demo
 */
const checkboxValueToJSON = (com: any, value: number[]) => {
  const { propertyConfigOptionVOList: options } = com;
  if (!(isArray(options) && options.length)) return null;
  // 查找对应的选项
  const targetOptions = options.filter((item) => value.includes(item.id));
  if (targetOptions.length) {
    const selectedValues:any = targetOptions.map((item) => {
      // 处理勾选了“其他”选项
      if (item.id === OTHER_TYPE_ID) {
        const inputValue = com[OTHER_TYPE_INPUT_TEXT];
        delete com[OTHER_TYPE_INPUT_TEXT];
        return {
          selectedId: OTHER_TYPE_ID,
          name: OTHER_TYPE_NAME,
          input: inputValue
        };
      } else {
        return {
          selectedId: item.id,
          name: item.name
        };
      }
    });
    // 和接口约定的格式
    return JSON.stringify(selectedValues);
  }
  return null;
};
// 数字输入框组件的赋值
const inputNumberValueToJSON = (com: any, value: any) => {
  const { restriction } = com;
  let parsedRestriction;
  // 防止数据异常，JSON.parse的出错
  try {
    parsedRestriction = JSON.parse(restriction);
  } catch (error) {}
  if (!parsedRestriction) return null;
  const { suffixOptionList } = parsedRestriction;
  const suffix = isArray(suffixOptionList) && suffixOptionList.length ? suffixOptionList[0].name : null;
  return JSON.stringify({
    value,
    suffix
  });
};
// 上传组件的赋值
export const uploadValToJSON = (value: any[]) => {
  if (!(isArray(value) && value.length)) return null;
  const jsonData = value.map((item) => ({
    name: item.name,
    url: item.url
  }));
  return JSON.stringify(jsonData);
};

// 控件类型
export const ControlType = {
  // csName 待定，可能不需要
  SINGLE_RADIO: { value: 1, name: '单选框', csName: '' },
  CHECK_BOX: { value: 2, name: '多选框', csName: '' },
  INPUT: { value: 3, name: '输入框', csName: '' },
  TEXT_AREA: { value: 4, name: '文本框', csName: '' },
  UPLOAD: { value: 5, name: '文件上传', csName: '' },
  INPUT_NUMBER: { value: 7, name: '数字输入框', csName: '' },
  TIME: { value: 9, name: '时间', csName: '' },
  ADDRESS: { value: 10, name: '详细地址', csName: '' },
  /** 省市区（会和商圈关联） */
  AREA: { value: 14, name: '省市区', csName: '' },
  TREE_SELECT: { value: 21, name: '树形选择', csName: 'cs_tree_select' },
  SURROUND_SEARCH: { value: 25, name: '周边查询', csName: 'cs_surround_search' },
  FOOTPRINT: { value: 26, name: '踩点类型', csName: 'cs_footprint' },
  SUB_FORM: { value: 27, name: '子表单', csName: 'cs_sub_form' },
  BUSINESS_PLANNING: { value: 28, name: '网规组件', csName: 'cs_network_component' },
  SALE_AMOUNT: { value: 29, name: '销售额预测', csName: 'cs_sale_amount' },
  MATCH_BUSINESS_CIRCLE: { value: 30, name: '匹配商圈', csName: 'cs_match_business_circle' },
  DAILY_FLOW_PREDICT: { value: 31, name: '日均客流预测', csName: 'cs_daily_flow_predict' },
  REFERENCE_CONVERSION: { value: 32, name: '参照转化率', csName: '' },
  REFERENCE_RENT: { value: 33, name: '参照租金', csName: '' },
  BENCH_MARKING_BRANDS: { value: 34, name: '对标品牌', csName: '' },
  PROFIT_CAlCULATION_JNP: { value: 35, name: '基诺浦盈亏计算', csName: '' },
  ESTIMATE_SALES_KWK: { value: 37, name: '卡旺卡门店预计销售额', csName: 'cs_estimate_sales_kwk' },
  ESTIMATE_SALES_YHT: { value: 38, name: '益禾堂预估销售额', csName: 'cs_estimate_sales_yht' },
  POPULATION_FLOW_YHT: { value: 39, name: '益禾堂人口流量', csName: 'cs_population_flow_yht' },
  // https://confluence.lanhanba.com/pages/viewpage.action?pageId=132274569
  ESTIMATE_SALES_TWO_YHT: { value: 40, name: '营业额系统预估2', csName: 'cs_estimate_sales_two_yht' },
  ESTIMATE_SALES_PERSONAL_YHT: { value: 41, name: '营业额预估', csName: 'cs_estimate_sales_personal_yht' },
  ESTIMATE_SALES_MT_MEAN_YHT: { value: 42, name: '美团竞品均值', csName: 'cs_estimate_sales_mt_mean_yht' },
  ESTIMATE_SALES_MT_MAX_YHT: { value: 43, name: '美团竞品高值', csName: 'cs_estimate_sales_mt_max_yht' },
  ESTIMATE_SALES_CONCLUSION_YHT: { value: 44, name: '营业额预估结论值', csName: 'cs_estimate_sales_conclusion_yht' },
  ESTIMATE_SALES_DELIVERY_YHT: { value: 45, name: '外卖额预估', csName: 'cs_estimate_sales_delivery_yht' },
  PASSENGER_FLOW: { value: 46, name: '客流计数', csName: 'cs_passenger_flow' },
  COMPETITIVE_CONDITIONS: { value: 47, name: '竞争条件', csName: 'cs_competitive_condition' },
  CONTEND_INFO: { value: 48, name: '竞品信息', csName: 'competitor_info' },
  /** 独立的省市区（不关联商圈） */
  CUSTOM_AREA: { value: 49, name: '自定义省市区', csName: 'cs_custom_area' },
  /** 加盟商合作进展（一鸣） */
  FRANCHISEE_COOPERATION_PROGRESS: { value: 50, name: '加盟商合作进展', csName: 'cs_franchisee_cooperation_progress' },
  /** 公司成员 */
  COMPANY_MEMBER: { value: 51, name: '公司成员', csName: 'cs_company_member' },

  /** AI预测进店率 */
  AI_PREDICT_ENTRY_RATE: { value: 57, name: 'AI预测进店率', csName: 'cs_ai_predict_entry_rate' },
  /** 区域保护（防撞店：三津汤包） */
  AREA_PROTECTION: { value: 58, name: '区域保护', csName: 'cs_area_protection' },
  /** 起止租期 */
  LEASE_TERM: { value: 59, name: '起止租期', csName: 'cs_lease_term' },
  /** 关联踩点记录 */
  ASSOCIATED_RECORDS: { value: 60, name: '关联踩点记录', csName: 'cs_associated_records' },
  /** 商务条件：计费方式 + 月租金 + 扣点比例（复合组件），计费方式联动其余字段显隐 */
  BUSINESS_CONDITION: { value: 61, name: '商务条件', csName: 'cs_business_condition' },
  /** 公共项目与租户点位的复合选择控件。 */
  PROJECT_SPOT_SELECT: { value: 62, name: '选择项目点位', csName: 'cs_project_spot_select' },
  /** 推荐机会点（专为一鸣加盟商表单定制） */
  RECOMMEND_CHANCE_POINT: { value: 63, name: '推荐机会点', csName: 'cs_recommend_chance_point' },
};

/**
 * @description 将动态表单中的所有组件平铺到数组中，方便查找到对应的组件
 * @param {Array} coms 接口返回的动态表单组件的数据
 * @param {any} tiledFormDataRef 平铺的对象
 */
export const formDataComsToTiled = (coms: any[], tiledFormDataRef: any) => {
  isArray(coms) && coms.forEach((item: any) => {
    const { childList, identification, restriction, templateRestriction, controlType, formConfigList } = item;
    if (identification) {
      /**
       * 遍历到字段的层级时，手动合并templateRestriction至restriction
       * restriction 存放的是属性字段相关的配置（基础配置）
       * templateRestriction 存放的是模版里字段的配置（模板字段取值来源是属性字段，故这里的配置是优先级最高的）
       * 但由于某些历史原因，代码里用的都是restriction，故需要一次合并
       */
      let mergeRestriction = null;
      let targetTypeAndData: any = null;
      // 单选或多选时
      if (controlType === ControlType.SINGLE_RADIO.value || controlType === ControlType.CHECK_BOX.value) {
        // 配置了其他
        if (restriction && isArray(JSON.parse(restriction)) && restriction?.includes('withOther')) {
          targetTypeAndData = JSON.stringify({
            withOther: true
          });
        } else if (isNotEmpty(restriction)) {
          targetTypeAndData = restriction;
        }
      } else {
        targetTypeAndData = restriction;
      }
      try {
        mergeRestriction = Object.assign(isNotEmpty(targetTypeAndData) ? JSON.parse(targetTypeAndData) : {}, isNotEmpty(templateRestriction) ? JSON.parse(templateRestriction) : {});
      } catch (error) {
        console.log(`合并restriction出错，唯一标识符${identification}`, error);
      }
      item.restriction = mergeRestriction ? JSON.stringify(mergeRestriction) : null;
      tiledFormDataRef.push(item);
    }
    isArray(childList) && childList.forEach((childItem: any) => {
      return formDataComsToTiled(childItem.propertyConfigVOList, tiledFormDataRef);
    });

    // 在上述处理完子项之后，处理子表单内的配置
    if (controlType === ControlType.SUB_FORM.value && Array.isArray(formConfigList)) {
      // 如果是子表单，将子表单内的选项字段重新赋值
      formConfigList.forEach((formConfigItem: any) => {
        // 优先级：templateRestriction（定制配置） > restriction（默认配置）
        const { controlType, templateRestriction, restriction } = formConfigItem;
        // formConfigItem.propertyConfigOptionVOList = propertyOptionList;
        let _restriction = isNotEmpty(restriction) ? JSON.parse(restriction) : {};
        // 单选或多选时，且配置是数组时
        if ([ControlType.SINGLE_RADIO.value, ControlType.CHECK_BOX.value].includes(controlType) && Array.isArray(_restriction)) {
          _restriction = _restriction.reduce((prev, cur) => ({ ...prev, [cur]: true }), {});
        }
        const mergeRestriction = Object.assign({},
          _restriction,
          isNotEmpty(templateRestriction) ? JSON.parse(templateRestriction) : {},
        );
        switch (controlType) {
          case ControlType.CHECK_BOX.value: {
            Object.assign(mergeRestriction, {
              selectMin: mergeRestriction.selectMin,
              selectMax: mergeRestriction.selectMax,
            });
            break;
          }
          case ControlType.INPUT_NUMBER.value: {
            Object.assign(mergeRestriction, {
              precision: { decimals: mergeRestriction.precision?.decimals },
            });
            break;
          }
          default:
            break;
        }
        formConfigItem.restriction = JSON.stringify(mergeRestriction || {});
      });
    }

  });
};

/** 只读检测动态表单配置中是否存在指定控件类型。 */
export const hasDynamicControlType = (coms: any[], targetControlType: number) => {
  if (!isArray(coms)) return false;

  return coms.some((item: any) => {
    // 关联显隐会将字段标记为 false；隐藏字段不能启用防撞店业务，也不能继续扫描其子字段。
    if (item?.isShow === false) return false;
    if (item?.controlType === targetControlType) return true;
    if (hasDynamicControlType(item?.formConfigList, targetControlType)) return true;

    return isArray(item?.childList) && item.childList.some((childItem: any) => {
      return hasDynamicControlType(childItem?.propertyConfigVOList, targetControlType);
    });
  });
};

/** 区域保护必须与详细地址组件同时配置，才启用防撞店业务逻辑。 */
export const hasAreaProtectionAndAddress = (coms: any[]) => {
  return hasDynamicControlType(coms, ControlType.AREA_PROTECTION.value) && hasDynamicControlType(coms, ControlType.ADDRESS.value);
};

/**
 * 组件的赋值操作，用来提交给接口（textvalue的赋值）：将 textValue 转换为 json 格式
 * @description 目前的实现方式是：组件变更，触发onValuesChange事件，在事件中调用setComValue方法，将组件的值赋值给组件的textValue属性，在提交的时候不做处理。
 * 但这样导致每次变更的成本增加，更好的实现应该是在提交的时候（getPropertyValuesByTiledFormData）只进行一次统一处理 textValue。
 * @param {Object} targetCom 对应的组件
 * @param {any} value 当前组件的值
 */
export const setComValue = (targetCom: any, value: any) => {
  if (!targetCom) return;
  const {
    controlType,
  } = targetCom;
  if (controlType === ControlType.LEASE_TERM.value &&
    (!(isArray(value) && value.length === 2 && value.every(item => isNotEmpty(item))))) {
    targetCom.textValue = null;
    return;
  }
  if (isNotEmpty(value)) {
    // 需要对每个组件的textValue进行处理（和服务端约定是JSON字符串）
    switch (controlType) {
      case ControlType.SINGLE_RADIO.value: // 单选
        targetCom.textValue = singleValueToJSON(targetCom, value);
        break;
      case ControlType.CHECK_BOX.value: // 多选
        targetCom.textValue = checkboxValueToJSON(targetCom, value);
        break;
      case ControlType.INPUT.value: // 输入框
      case ControlType.ESTIMATE_SALES_TWO_YHT.value:
      case ControlType.ESTIMATE_SALES_PERSONAL_YHT.value:
      case ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value:
      case ControlType.ESTIMATE_SALES_MT_MAX_YHT.value:
      case ControlType.ESTIMATE_SALES_DELIVERY_YHT.value:
        targetCom.textValue = value;
        break;
      case ControlType.TEXT_AREA.value: // 文本域
        targetCom.textValue = value;
        break;
      case ControlType.INPUT_NUMBER.value: // 数字输入框
        targetCom.textValue = inputNumberValueToJSON(targetCom, value);
        break;
      case ControlType.TIME.value: // 日期组件
        targetCom.textValue = dayjs(value).format('YYYY-MM-DD');
        break;
      case ControlType.UPLOAD.value:
        targetCom.textValue = uploadValToJSON(value);
        break;
      case ControlType.FOOTPRINT.value:
        targetCom.textValue = value;
        break;
      case ControlType.ADDRESS.value: // 地址组件
      case ControlType.TREE_SELECT.value: // 树形选择
      case ControlType.AREA.value: // 省市区
      case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
      case ControlType.SURROUND_SEARCH.value: // 周边查询组件
      case ControlType.SUB_FORM.value: // 子表单
      case ControlType.CONTEND_INFO.value: // 店铺周边竞品
      case ControlType.BUSINESS_PLANNING.value: // 商圈规划
      case ControlType.SALE_AMOUNT.value: // 销售额
      case ControlType.MATCH_BUSINESS_CIRCLE.value: // 商圈信息
      case ControlType.DAILY_FLOW_PREDICT.value:
      case ControlType.BENCH_MARKING_BRANDS.value:
      case ControlType.ESTIMATE_SALES_KWK.value:
      case ControlType.COMPETITIVE_CONDITIONS.value: // 竞争条件
        // console.log('value', value);
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.PROFIT_CAlCULATION_JNP.value:
        targetCom.textValue = JSON.stringify(value, (key, val) => typeof val === 'undefined' ? null : val); // undefine转化null，避免字段丢失
        break;
      case ControlType.ESTIMATE_SALES_YHT.value:
      case ControlType.POPULATION_FLOW_YHT.value:
      case ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value:
      case ControlType.PASSENGER_FLOW.value:
      case ControlType.FRANCHISEE_COOPERATION_PROGRESS.value:
      {
        const oldValue = parseValueCatch(targetCom) || {};
        targetCom.textValue = JSON.stringify({ ...oldValue, ...value });
        break;
      }
      case ControlType.COMPANY_MEMBER.value:
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.AI_PREDICT_ENTRY_RATE.value:
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.AREA_PROTECTION.value:
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.ASSOCIATED_RECORDS.value:
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.PROJECT_SPOT_SELECT.value:
      case ControlType.BUSINESS_CONDITION.value:
        targetCom.textValue = JSON.stringify(value);
        break;
      case ControlType.LEASE_TERM.value: {
        const [startDate, endDate] = isArray(value) ? value : [];
        targetCom.textValue = JSON.stringify({
          startDate,
          endDate,
          duration: getLeaseTermDuration(value),
        });
        break;
      }
    }
    return;
  }
  targetCom.textValue = isUndef(value) ? null : '';
};

/** 根据 controlType 获取 textValue 的初始值 */
export const getTextValueInitValue = (controlType: number) => {
  let textValue: any = null;
  // 为空时，赋予默认值
  switch (controlType) {
    case ControlType.SINGLE_RADIO.value:
      textValue = null;
      break;
    case ControlType.CHECK_BOX.value:
      textValue = [];
      break;
    case ControlType.INPUT.value:
    case ControlType.TEXT_AREA.value:
      textValue = null;
      break;
    case ControlType.UPLOAD.value:
      textValue = [];
      break;
    case ControlType.INPUT_NUMBER.value:
    case ControlType.ADDRESS.value:
    case ControlType.AREA.value:
    case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
      textValue = null;
      break;
    case ControlType.SUB_FORM.value:
      textValue = [];
      break;
    case ControlType.COMPANY_MEMBER.value:
      textValue = [];
      break;
    case ControlType.LEASE_TERM.value:
      textValue = [];
      break;
    case ControlType.AI_PREDICT_ENTRY_RATE.value:
      textValue = null;
      break;
    case ControlType.ASSOCIATED_RECORDS.value:
      textValue = null;
      break;
    case ControlType.PROJECT_SPOT_SELECT.value:
    case ControlType.BUSINESS_CONDITION.value:
      textValue = null;
      break;
    default:
      break;
  }
  return textValue;
};

/**
 * 获取动态表单组件的默认值（可用于子表单的新增分组初始化值）
 * @description
 */
export const getItemDefaultValue = (item) => {
  const { restriction, controlType, formConfigList } = item;
  const defaultValue = JSON.parse(restriction || '{}')?.defaultValue;
  // 根据组件类型，将表单数据set到form上
  isDef(defaultValue) ? setComValue(item, defaultValue) : undefined; // 将defaultValue赋给textValue
  // 处理子表单内各组件的默认值
  if (controlType === ControlType.SUB_FORM.value && Array.isArray(formConfigList)) {
    const textValue = [{}];
    formConfigList.forEach((subItem: any) => {
      const defaultValue = JSON.parse(subItem?.restriction || '{}')?.defaultValue;
      isDef(defaultValue) ? setComValue(subItem, defaultValue) : undefined; // 将defaultValue赋给textValue
      // 处理成详情返回的格式，方便使用详情回显的函数（formDataEcho）回显到表单
      textValue.push({
        controlType: subItem.controlType,
        id: subItem.id,
        identification: subItem.identification,
        propertyId: subItem.propertyId,
        row: 0,
        textValue: subItem.textValue,
      });
    });
    item.textValue = JSON.stringify(textValue);
  }
  const _defaultValue = formDataEcho(item); // 这里主要针对存在 defaultValue 的控件，以及子表单控件
  return _defaultValue;
};

/**
 * 获取创建时的默认数据到表单（从模板详情接口返回的平铺数据中获取）
 * @param tiledFormData 传入 tiledFormDataRef.current
 * @returns
 * @demo
    const values = getEditDefaultValuesByTiledFormData(tiledFormDataRef.current);
    form.setFieldsValue(values);
 */
export const getEditDefaultValuesByTiledFormData = (tiledFormData: any[]) => {
  const values = {};
  Array.isArray(tiledFormData) && tiledFormData.forEach((item: any) => {
    const { identification } = item;
    console.log('identification', identification, getItemDefaultValue(item));
    values[identification] = getItemDefaultValue(item);
  });
  return values;
};

/**
 * 获取编辑时的编辑详情数据到表单（从详情接口返回的平铺数据中获取）
 * @param tiledFormData 传入 tiledFormDataRef.current
 * @returns
 * @demo
    const values = getEditValuesByTiledFormData(tiledFormDataRef.current);
    form.setFieldsValue(values);
 */
export const getEditValuesByTiledFormData = (tiledFormData: any[]) => {
  const values = {};
  Array.isArray(tiledFormData) && tiledFormData.forEach((item: any) => {
    const { identification } = item;
    // 根据组件类型，将表单数据set到form上
    values[identification] = formDataEcho(item);
    if (item.controlType === ControlType.SINGLE_RADIO.value) {
      // 处理单选的“其他”输入值到 form
      const _identification = identification + OTHER_TYPE_INPUT_TEXT;
      const _textValue = parseValueCatch(item);
      values[_identification] = _textValue?.input || '';
    } else if (item.controlType === ControlType.CHECK_BOX.value) {
      // 处理单选的“其他”输入值到 form
      const _identification = identification + OTHER_TYPE_INPUT_TEXT;
      const _textValue = parseValueCatch(item);
      const _otherItem = (Array.isArray(_textValue) ? _textValue : []).find(it => it.selectedId === OTHER_TYPE_ID);
      values[_identification] = _otherItem?.input || '';
    }
  });
  return values;
};

/**
 * 获取新增/编辑提交给接口的数据（从平铺数据中获取）
 * @param tiledFormData 传入 tiledFormDataRef.current
 * @returns
 * @demo
    const propertyValues = getSubmitValuesByTiledFormData(tiledFormDataRef.current);
 */
export const getSubmitValuesByTiledFormData = (tiledFormData: any[]) => {
  return Array.isArray(tiledFormData) ? tiledFormData.map((item: any) => {
    let textValue = item.textValue;
    /**
     * 处理子表单数据（接口要求）
     * 同步 store-assistant-h5/src/common/components/DynamicForm/config.ts 的 getPropertyValuesForSubmit
     * [{
        'isHeadquarters': 1134,
        'locationTestChekbox': [2048, 2049],
        'name': '123123131321321',
        'checkBizOtherPropertyFee': '31313131231',
        'builtArea': 11,
        'checkBizPlannedOpeningTimeTwo': dayjs('2025-11-21')
      }];
      => [
        { "controlType": 1, "id": 381, "identification": "isHeadquarters", "propertyId": 52, "row": 0, "textValue": "{\"selectedId\":1134,\"name\":\"非总部\"}" },
        { "controlType": 2, "id": 382, "identification": "locationTestChekbox", "propertyId": 737, "row": 0, "textValue": "[{\"selectedId\":2048,\"name\":\"刘德华\"},{\"selectedId\":2049,\"name\":\"黎明\"}]" },
      ]
     */
    if (item.controlType === ControlType.SUB_FORM.value) {
      // 子表单组件配置映射表，用于下面转换组表单组件的值
      const formConfigListMap = Array.isArray(item.formConfigList) ? deepCopy(item.formConfigList).reduce((pre: any, cur: any) => {
        pre[cur.identification] = cur;
        return pre;
      }, {}) : {};
      console.log('formConfigListMap', formConfigListMap);
      const _values = JSON.parse(textValue || '[]') || [];
      console.log('_values', _values);
      /** 将子表单数组的子项字段抽离出来平铺，格式如 [{ id, propertyId, controlType, identification, row, textValue}] */
      const data = Array.isArray(_values) ? _values.reduce((pre, cur, index) => {
        /** 子表单子项的各组件字段进行平铺，并结合 formConfigListMap 转换为对象形式，格式如 [{ id, propertyId, controlType, identification, row, textValue}] */
        const subFormItems = Object.keys(cur).map((key) => {
          // 使用 setComValue 转换为字符串
          const formConfigItem = deepCopy(formConfigListMap[key]);
          if (!formConfigItem) { // 过滤掉没有 identification 的项，比如 单选多选的其他输入框 xxx_otherTypeInputText_no_repeat
            return null;
          }
          if (formConfigItem.controlType === ControlType.SINGLE_RADIO.value) {
            // 处理“其他”
            // singleValueToJSON 从 formConfigItem 获取“其他”
            formConfigItem[OTHER_TYPE_INPUT_TEXT] = cur[key + OTHER_TYPE_INPUT_TEXT] || null;
          } else if (formConfigItem.controlType === ControlType.CHECK_BOX.value) {
            // 处理“其他”
            // singleValueToJSON 从 formConfigItem 获取“其他”
            formConfigItem[OTHER_TYPE_INPUT_TEXT] = cur[key + OTHER_TYPE_INPUT_TEXT] || null;
          }
          setComValue(formConfigItem, cur[key]);
          return {
            // ...formConfigItem,
            id: formConfigItem.id,
            propertyId: formConfigItem.propertyId,
            controlType: formConfigItem.controlType,
            identification: key,
            row: index,
            textValue: formConfigItem.textValue, // 这里是处理 cur[key] 后的值
          };
        }).filter((item: any) => Boolean(item));
        return pre.concat(subFormItems);
      }, []) : [];
      console.log('处理后提交的子表单数据', data);
      textValue = JSON.stringify(data);
    }
    // 商务条件：隐藏字段不参与提交，避免切换计费方式后残留无效的月租金/扣点比例
    if (item.controlType === ControlType.BUSINESS_CONDITION.value) {
      const businessConditionValue = parseValueCatch(item) || {};
      const { type, rent, discount } = businessConditionValue;
      textValue = JSON.stringify({
        type,
        rent: isShowRent(type) ? rent : null,
        discount: isShowDiscount(type) ? discount : null,
      });
    }
    return {
      id: item.id,
      propertyId: item.propertyId,
      controlType: item.controlType,
      identification: item.identification,
      textValue,
    };
  }) : [];
};

/**
 * 解析json格式的textValue
 * @param com 组件
 * @returns value
 */
export const parseValueCatch = (com: any, targetFields?: string) => {
  const {
    textValue,
    name,
    identification
  } = com;
  let value: any = null;
  try {
    value = targetFields ? JSON.parse(com[targetFields])
      : (isNotEmpty(textValue) ? JSON.parse(textValue) : textValue);
  } catch (error) {
    console.log(`${name}解析出错，唯一标识符${identification}`, error);
  }
  return value;
};

const getInputNumberValue = (targetCom: any) => {
  const inputNumberValue = parseValueCatch(targetCom);
  if (isNotEmpty(inputNumberValue?.value)) return inputNumberValue.value;
  if (typeof inputNumberValue === 'number' || typeof inputNumberValue === 'string') return inputNumberValue;
  return '';
};

const getInputNumberShowValue = (targetCom: any) => {
  const inputNumberValue = parseValueCatch(targetCom);
  if (isNotEmpty(inputNumberValue?.value)) return `${inputNumberValue.value}${inputNumberValue.suffix || ''}`;
  if (typeof inputNumberValue === 'number' || typeof inputNumberValue === 'string') return `${inputNumberValue}`;
  return '';
};

/**
 * 表单数据 textValue 的回显（编辑时的数据回显）
 * @description 解析 json 格式的 textValue，并对 textValue 为空的字段返回初始化值，如 数组等
 * @param {any} targetCom
 * @return {Object}
 */
export const formDataEcho = (targetCom: any) => {
  const {
    controlType,
    textValue,
  } = targetCom;
  // 子表单比较特殊，值来自子组件的 textValue
  if (isNotEmpty(textValue) || controlType === ControlType.SUB_FORM.value) {
    // 需要对每个组件的textValue进行处理，使其在表单中回显
    switch (controlType) {
      case ControlType.SINGLE_RADIO.value: // 单选
        const singleValue: any = parseValueCatch(targetCom);
        return singleValue?.selectedId || null;
      case ControlType.CHECK_BOX.value: // 多选
        const checkboxValue: any = parseValueCatch(targetCom);
        return checkboxValue?.map((item: any) => item.selectedId) || [];
      case ControlType.INPUT.value: // 输入框
        return textValue || '';
      case ControlType.TEXT_AREA.value: // 文本域
      case ControlType.ESTIMATE_SALES_KWK.value: // 卡旺卡门店预估销售额
        return textValue || '';
      case ControlType.INPUT_NUMBER.value: // 数字输入框
        return getInputNumberValue(targetCom);
      case ControlType.TIME.value: // 日期组件
        return textValue ? dayjs(textValue) : '';
      case ControlType.ADDRESS.value: // 地址组件
        const addressValue = parseValueCatch(targetCom);
        return addressValue?.poiName || addressValue?.address || '';
      case ControlType.AREA.value: // 省市区
      case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
        // const pcdValue: any = parseValueCatch(targetCom);
        // const {
        //   provinceId,
        //   cityId,
        //   districtId
        // } = pcdValue || {};
        // return provinceId && cityId && districtId ? [provinceId, cityId, districtId] : [];
        // 组件内部已经改为接收 { provinceId, cityId, districtId } 格式
        return parseValueCatch(targetCom) || [];
      case ControlType.UPLOAD.value:
        return parseValueCatch(targetCom) || [];
      case ControlType.TREE_SELECT.value:
        return parseValueCatch(targetCom);
      case ControlType.FOOTPRINT.value:// 踩点组件
        return targetCom;
      case ControlType.SURROUND_SEARCH.value: // 周边查询
        return targetCom;
      case ControlType.CONTEND_INFO.value: // 店铺周边竞品
      case ControlType.COMPETITIVE_CONDITIONS.value: // 竞争条件 COMPETITIVE_CONDITIONS
        return parseValueCatch(targetCom) || [];
      case ControlType.BUSINESS_PLANNING.value: // 商圈规划
      case ControlType.SALE_AMOUNT.value: // 销售额
      case ControlType.MATCH_BUSINESS_CIRCLE.value: // 商圈信息
      case ControlType.DAILY_FLOW_PREDICT.value: // 客流预测
      case ControlType.REFERENCE_CONVERSION.value: // 参考转化率
      case ControlType.REFERENCE_RENT.value: // 参考租金
      case ControlType.PROFIT_CAlCULATION_JNP.value: // 基诺浦盈亏测算
        return targetCom;
      case ControlType.BENCH_MARKING_BRANDS.value: // 对标品牌
        return parseValueCatch(targetCom) || [];
      case ControlType.ESTIMATE_SALES_YHT.value: // 预估益禾堂
      case ControlType.ESTIMATE_SALES_TWO_YHT.value:
      case ControlType.ESTIMATE_SALES_PERSONAL_YHT.value:
      case ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value:
      case ControlType.ESTIMATE_SALES_MT_MAX_YHT.value:
      case ControlType.ESTIMATE_SALES_DELIVERY_YHT.value:
      case ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value:
      case ControlType.PASSENGER_FLOW.value:
      case ControlType.FRANCHISEE_COOPERATION_PROGRESS.value:
      case ControlType.AI_PREDICT_ENTRY_RATE.value:
        return parseValueCatch(targetCom);
      case ControlType.ASSOCIATED_RECORDS.value:
        return parseValueCatch(targetCom);
      case ControlType.COMPANY_MEMBER.value:
        return parseValueCatch(targetCom) || [];
      case ControlType.LEASE_TERM.value: {
        const leaseTermValue = parseValueCatch(targetCom);
        return leaseTermValue?.startDate && leaseTermValue?.endDate ? [leaseTermValue.startDate, leaseTermValue.endDate] : [];
      }
      case ControlType.PROJECT_SPOT_SELECT.value:
      case ControlType.BUSINESS_CONDITION.value: {
        return parseValueCatch(targetCom) || {};
      }
      // 子表单的默认值
      case ControlType.SUB_FORM.value:{
        /** 逻辑同步 store-assistant-h5/src/common/components/DynamicForm/config.ts 的 decodeTextValue
        [
          { "controlType": 1, "id": 381, "identification": "isHeadquarters", "propertyId": 52, "row": 0, "textValue": "{\"selectedId\":1134,\"name\":\"非总部\"}" },
          { "controlType": 2, "id": 382, "identification": "locationTestChekbox", "propertyId": 737, "row": 0, "textValue": "[{\"selectedId\":2048,\"name\":\"刘德华\"},{\"selectedId\":2049,\"name\":\"黎明\"}]" },
        ]
        =>
        [{'isHeadquarters': 1134,'locationTestChekbox': [2048, 2049],'name': '123123131321321','checkBizOtherPropertyFee': '31313131231','builtArea': 11,'checkBizPlannedOpeningTimeTwo': dayjs('2025-11-21')}]; */
        const _textValue = parseValueCatch(targetCom);
        console.log('处理子表单回显数据 _textValue', _textValue);
        let data = [{}];
        if (Array.isArray(_textValue)) {
          data = _textValue.reduce((prev: any[], cur: any) => {
            if (isNotEmpty(cur.row) && !isNotEmpty(prev[cur.row])) {
              prev[cur.row] = {};
            }
            if (isNotEmpty(prev[cur.row])) {
              const value = formDataEcho(cur);
              if (cur.controlType === ControlType.SINGLE_RADIO.value) {
                // 处理“其他”输入框
                const temp = parseValueCatch(cur);
                prev[cur.row][cur.identification + OTHER_TYPE_INPUT_TEXT] = temp?.input || '';
              } else if (cur.controlType === ControlType.CHECK_BOX.value) {
                // 处理“其他”输入框
                const temp = parseValueCatch(cur);
                const otherItem = (Array.isArray(temp) ? temp : []).find(item => item.selectedId === OTHER_TYPE_ID);
                prev[cur.row][cur.identification + OTHER_TYPE_INPUT_TEXT] = otherItem?.input || '';
              }
              prev[cur.row][cur.identification] = value;
            }
            return prev;
          }, [{}]);
        }
        console.log('处理后的子表单回显数据 data', JSON.parse(JSON.stringify(data)));
        return data;
      }
    }
  }
  return getTextValueInitValue(controlType);
};

/**
 * @description 关联属性的显示逻辑，只有单选/多选才会根据选择项去配置关联显示的字段，所以遍历时只需要解析被关联的字段中是和哪个单选/多选组件的值绑定显示逻辑了
 * @param {any} coms
 */
export const relevancyShow = (coms: any[], restoreDefaultValues = false, restoreDefaultIdentifications?: string[]) => {
  const hiddenComs = restoreDefaultValues && isArray(coms) ? coms.filter((comItem: any) => !comItem.isShow) : [];
  coms.forEach((comItem: any) => {
    const {
      templateRestrictionPassiveList,
      name,
    } = comItem;
    // 没有关联显示的配置时，设置为true
    if (!templateRestrictionPassiveList) {
      comItem.isShow = true;
      return;
    }
    try {
      // 关联的配置
      const relevancyConfig: any[] | null = templateRestrictionPassiveList;
      if (!isArray(relevancyConfig)) return;
      // 当前组件被某个组件关联时的配置
      relevancyConfig.forEach((relevancyItem: any) => {
        // 源组件（单选/多选），里面包含了选择某项值时哪些组件关联显示
        const relevancyOriginCom = coms.find((item: any) => item.propertyId === relevancyItem.relationsComponent);
        if (!relevancyOriginCom) return;

        const { controlType } = relevancyOriginCom;
        const { currentValue } = relevancyItem;
        // 解析源组件的值
        const value: any = parseValueCatch(relevancyOriginCom);
        if (!value) {
          comItem.isShow = false;
          comItem.textValue = null;
          return;
        }
        // 单选
        if (controlType === ControlType.SINGLE_RADIO.value) {
          const { selectedId } = value;
          if (currentValue.includes(selectedId)) {
            comItem.isShow = true;
            return;
          }
        }
        // 多选
        if (controlType === ControlType.CHECK_BOX.value) {
          const selectedIds = value.map((item) => item.selectedId);
          if (!selectedIds.length) {
            comItem.isShow = false;
            return;
          }

          if (currentValue.some((val: any) => selectedIds.includes(val))) {
            comItem.isShow = true;
          } else {
            comItem.isShow = false;
          }
          return;
        }
        comItem.isShow = false;
        comItem.textValue = null;
      });
    } catch (error) {
      console.log(`${name}解析关联显示出错,被${templateRestrictionPassiveList.map((item) => item.relationsComponent).join('、')}关联`, error);
    }
  });
  return hiddenComs.reduce((values: any, comItem: any) => {
    // 字段从隐藏切换为显示时，仅在原值已被隐藏逻辑清空的情况下恢复默认值。
    if (!comItem.isShow || isNotEmptyAny(comItem.textValue)) return values;
    if (isArray(restoreDefaultIdentifications) && !restoreDefaultIdentifications.includes(comItem.identification)) return values;
    const defaultValue = getItemDefaultValue(comItem);
    if (isDef(defaultValue)) {
      values[comItem.identification] = defaultValue;
    }
    return values;
  }, {});
};


/**
 * @description 动态表单-列表的动态表头数据解析展示
 * @param {any} targetCom
 */
export const analysisTableTitle = (targetCom:any) => {
  const {
    controlType,
    textValue,
    /** 地址组件优先级，如果为 true，优先级 address > poiName */
    priorityAddress,
  } = targetCom;
  if (isNotEmpty(textValue)) {
    // 需要对每个组件的textValue进行处理，使其在表单中回显
    switch (controlType) {
      case ControlType.SINGLE_RADIO.value: // 单选
      // 对象  {selectedId: 2214, name: '符合'} 显示name
        const singleValue: any = parseValueCatch(targetCom);
        return singleValue?.name || null;

      case ControlType.CHECK_BOX.value: // 多选
        const checkboxValue: any = parseValueCatch(targetCom);
        const checkboxValues = isArray(checkboxValue) ? checkboxValue : [checkboxValue];
        const res = checkboxValues
          .map((item) => item?.name)
          .filter((item) => isNotEmpty(item))
          .join('、');
        // 数组、对象 selectedId,name，遍历展示所有name
        // return checkboxValue?.map((item: any) => item.selectedId) || [];
        return res;

      case ControlType.TREE_SELECT.value:
        const treeValue = parseValueCatch(targetCom);
        return (treeValue?.optionList || []).map(item => item.name).filter(isNotEmpty).join('、');

      case ControlType.INPUT.value: // 输入框
        return textValue || '';

      case ControlType.TEXT_AREA.value: // 文本域
        return textValue || '';

      case ControlType.INPUT_NUMBER.value: // 数字输入框
      // ok: inputNumberValue 对象 suffix 单位 value 数据 显示 value+suffix
        return getInputNumberShowValue(targetCom);

      case ControlType.TIME.value: // 日期组件
      // ok 直接显示 textValue
        return textValue || '';
      case ControlType.LEASE_TERM.value: {
        const leaseTermValue = parseValueCatch(targetCom);
        return leaseTermValue?.startDate && leaseTermValue?.endDate
          ? `${leaseTermValue.startDate.replace(/-/g, '.')}-${leaseTermValue.endDate.replace(/-/g, '.')}`
          : '';
      }
      case ControlType.ADDRESS.value: // 地址组件
        const addressValue = parseValueCatch(targetCom);
        return priorityAddress
          ? (addressValue?.address || addressValue?.poiName || '')
          : (addressValue?.poiName || addressValue?.address || '');
      case ControlType.AREA.value: // 省市区
      case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
        const pcdValue: any = parseValueCatch(targetCom);
        const { provinceName, cityName, districtName } = pcdValue || {};
        // 例如“北京市北京市西城区”只显示“北京市西城区”
        return [provinceName, provinceName === cityName ? '' : cityName, districtName].filter(Boolean).join('');
      case ControlType.UPLOAD.value: // 上传组件
        const PICTURE_REG = /(\.gif|\.jpeg|\.png|\.jpg|\.bmp|\.svg)$/;
        const files = parseValueCatch(targetCom)?.filter((item) => !!item?.url) || [];
        return {
          isFile: true,
          files,
          isAllImage: files.every((file) => PICTURE_REG.test(String(file?.name || '').toLowerCase())),
        };
      case ControlType.MATCH_BUSINESS_CIRCLE.value: // 匹配商圈
        const value = parseValueCatch(targetCom);
        return value?.clusterName;
      case ControlType.ESTIMATE_SALES_KWK.value:
        return isNotEmpty(textValue) ? `${textValue}元` : '-';
      case ControlType.FRANCHISEE_COOPERATION_PROGRESS.value: {
        const value = parseValueCatch(targetCom);
        // 门店序列-拓店状态（合作状态）
        let str = [value?.storeSequenceName, value?.storeExpansionStatusName].filter(Boolean).join('-');
        if (value?.cooperationStatus) {
          str = `${replaceEmpty(str)}（${value?.cooperationStatusName}）`;
        }

        return str;
      }
      case ControlType.COMPANY_MEMBER.value: // 公司成员
        // 目前没有用到，列表用到的时候再调试
        // [{ id: 2214, name: '张三' }]   显示name
        const data: any = parseValueCatch(targetCom);
        return parseObjectArrayToString(data, 'label');
    }
  }
  return null;
};

/**
 * @description 查找动态表单中所有配置了计算公式的组件
 * @param {any[]} coms 所有的动态表单组件
 * @return {any[]} 动态表单组件中所有配置了计算公式的组件
 */
export const getCalculateComs = (coms:any[]) => {
  let targetComs: any[] = [];
  try {
    targetComs = coms.filter((item: any) => {
      const { restriction } = item || {};
      const { expr }: any = restriction ? JSON.parse(restriction) : {};
      if (isArray(expr) && expr.length) return item;
    });
  } catch (error) {}
  return targetComs;
};

/**
 * @description 益禾堂租户时，检查匹配商圈组件选择地址是否在开店区域内
 * @param propertyItems 所有表单项
 * @return true 检查通过 false 未填写集客点
 */
export const checkMatchBusinessProperty = (isYiHeTang: boolean, propertyItems: any[]) => {
  if (!isYiHeTang || !isArray(propertyItems)) return true;

  const property = propertyItems.find((itm: any) => itm.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value);
  // 未找到该属性，终止检查
  if (!property) return true;

  const textValue = property.textValue;
  if (!textValue) return false; // 值异常，提示错误

  const valueObj = JSON.parse(textValue);

  // 检查1.是否在开店区域内，2.选择了集客点，或者有本地新建集客点数据
  const res = !!valueObj?.isInCluster && (!!valueObj.planSpotId || valueObj?.savePlanSpot?.planSpots?.[0].name);
  return res;
};

// 计算公式里的类型
enum CalculateType {
  Number = 'NUMBER', // 数字类型
  Operation = 'OPERATION', // 运算符类型
  Property = 'PROPERTY', // 绑定的需要计算的属性
}

// 计算公式的运算符
enum OperationalCharacter {
  Add = 100, // 加
  Subtract, // 减
  Multiply, // 乘
  Divide // 除
}

/**
 * @description 计算属性中关联的属性组件是否是百分比类型
 * @param {any} target 组件
 * @return {boolean} 是否是百分比类型的组件
 */
const targetComIsPercentage = (target: any) => {
  const { restriction } = target;
  const { suffixOptionList }: any = restriction ? JSON.parse(restriction) : {};
  return isArray(suffixOptionList) && suffixOptionList.length === 1 && suffixOptionList[0].name === '%';
};

// 获取计算公式中绑定的组件的值
const getCalculateBindVal = (curCom: any, objCalculateComs: any[]) => {
  const { identification } = curCom;
  const targetVal: any = parseValueCatch(curCom);
  if (identification === 'estimatedDailyRevenue') { // 日均销售额预测
    // 如果有值就用系统日均销售额预测的值
    if (targetVal?.value) return targetVal?.value;
    // 获取个人日均销售额预测
    const target: any = objCalculateComs.find((item: any) => item.identification === 'personalEstimatedDailyRevenue');
    if (!target) return '';
    return parseValueCatch(target)?.value;
  }
  if (identification === 'franchiseFeeYHT') { // 加盟费-益禾堂
    if (!targetVal) return 0;
    const value = targetVal?.name?.replace('元', '');
    // 判断下是否正确的数字
    if (!Number.isNaN(Number(value))) {
      return value;
    }
  }
  return targetVal?.value;
};

/**
 * @description 根据配置的计算公式，查找匹配的组件值，得出计算结果
 * @param {any} coms
 * @param {any} calculateComs
 */
export const calculateHandle = (
  objCalculateComs: any[], // 计算对象：所有的数字输入框组件+加盟费-益禾堂"franchiseFeeYHT"(下拉框)
  calculateComs: any[] // 所有配置了计算公式的组件
) => {
  if (!(isArray(objCalculateComs) && isArray(calculateComs))) return;
  calculateComs.forEach((itemCom: any) => {
    const { restriction } = itemCom;
    const { expr, precision }: any = restriction ? JSON.parse(restriction) : {}; // 存放计算公式的字段
    let afterCalculateVal = ''; // 计算公式字符串
    let hasVal = false;
    expr.forEach((exprItem: any, index: number) => {
      const {
        propertyId, // 属性id
        type, // 类型
        label,
        value // 值
      } = exprItem;
      let val = label;
      if (type === CalculateType.Property) { // 是计算公式里绑定的某个属性
        const curCom = objCalculateComs.find((item: any) => item.propertyId === propertyId); // 当前组件
        if (!curCom) return;
        const isHidden = curCom.isShow === false;
        // 百分比类型
        const isPercentage = !isHidden && targetComIsPercentage(curCom);
        // const curVal = parseValueCatch(curCom)?.value; // 当前组件的值
        const curVal = isHidden ? '' : getCalculateBindVal(curCom, objCalculateComs);
        val = curVal;

        if (isNotEmpty(val)) {
          hasVal = true;
        }
        const lastVal = index > 0 ? expr[index - 1].value : ''; // 上一个值
        const nextVal = expr[index + 1] ? expr[index + 1].value : ''; // 下一个值
        // // 查看当前属性是否是乘法里的乘数或被乘数
        if (lastVal === OperationalCharacter.Multiply || nextVal === OperationalCharacter.Multiply) { // 乘法
          // 未填写时赋值0
          if (!isNotEmpty(val)) val = 0;
        } else if (lastVal === OperationalCharacter.Divide || nextVal === OperationalCharacter.Divide) { // 除法里的除数和被除数 25/5 被除数/除数
          // 除数输入0时，赋值为1
          if (val === 0 && lastVal === OperationalCharacter.Divide) val = 1;
          // 被除数未输入时，赋值为0
          if (!isNotEmpty(val) && nextVal === OperationalCharacter.Divide) val = 0;
          // 除数未输入，赋值为1
          if (!isNotEmpty(val) && lastVal === OperationalCharacter.Divide) val = 1;

        } else if (lastVal === OperationalCharacter.Add || nextVal === OperationalCharacter.Add) { // 加法
          // 未填写时赋值0
          if (!isNotEmpty(val)) val = 0;
        } else if (lastVal === OperationalCharacter.Subtract || nextVal === OperationalCharacter.Subtract) { // 减法
          // 未填写时赋值0
          if (!isNotEmpty(val) && lastVal === OperationalCharacter.Subtract) val = 0;
        }
        // 百分比时除以100
        isPercentage && (val = val / 100);
      } else if (type === CalculateType.Operation) {
        value === OperationalCharacter.Multiply && (val = '*');
        value === OperationalCharacter.Divide && (val = '/');
      }

      afterCalculateVal += val;
    });
    // eslint-disable-next-line no-eval
    const totalVal = eval(afterCalculateVal);
    const { decimals } = precision || {};
    const formattingVal = Number(fixNumber(totalVal, !!decimals && decimals > 0 ? decimals : 0, true));
    // 计算公式关联的字段如果都未填写时不赋值
    if (!hasVal) return;
    if (!isFinite(+formattingVal)) return;
    setComValue(itemCom, formattingVal);
  });
};

/**
 * @description 动态表单属性字段是否可见
 */
export const isPropertyItemShow = ({
  propertyItem,
  supportDirectApproval,
  isChancepoint = true,
  isTraditionEvaluation = false,
} : {
  propertyItem: any;
  /** 是否支持直接提交审批 1:支持 2:不支持，默认值是2 */
  supportDirectApproval: number;
  /** 是否机会点表单，加盟商、踩点记录时false */
  isChancepoint: boolean;
  /** 是否传统的点位评估编辑页（不包含一鸣等租户走机会点点位评估审批流的场景），是的话item的显示、禁用，参照的是机会点拓店模板、非流程设置 */
  isTraditionEvaluation: boolean;
}) => {
  const { isShow } = propertyItem;
  if (!isShow) return false;

  // 支持提交审批时（即走配置的审批流时）
  if (isChancepoint && supportDirectApproval === 1 && !isTraditionEvaluation) {
    const { access } = propertyItem;
    // access 1：仅可见  2：可写 3:不可见 null:历史数据的不可见
    return access === 1 || access === 2;
  }
  return true;
};

/**
 * @description 动态表单属性字段是否禁用
 */
export const isPropertyItemDisable = ({
  propertyItem,
  supportDirectApproval,
  isChancepoint = true,
  isTraditionEvaluation = false,
} : {
  propertyItem: any;
  /** 是否支持直接提交审批 1:支持 2:不支持，默认值是2 */
  supportDirectApproval: number;
  /** 是否机会点表单，加盟商、踩点记录时false */
  isChancepoint: boolean;
  /** 是否传统的点位评估编辑页（不包含一鸣等租户走机会点点位评估审批流的场景），是的话item的显示、禁用，参照的是机会点拓店模板、非流程设置 */
  isTraditionEvaluation: boolean;
}) => {
  const { access } = propertyItem;
  // 支持提交审批时（即走配置的审批流时）；点位评估时，虽然支持审批，但是读取的是拓店模板设置，不参照流程设置
  if (isChancepoint && supportDirectApproval === 1 && !isTraditionEvaluation) {
    // access 1：仅可见  2：可写 3:不可见 null:历史数据的不可见
    return access === 1;
  }

  const restriction = propertyItem.restriction ? JSON.parse(propertyItem.restriction) : {};
  return !!restriction?.disable;
};

/**
 * @description 动态表单属性字段是否可见、且可编辑
 */
export const isPropertyItemVisibleEditable = ({
  propertyItem,
  supportDirectApproval,
  isChancepoint = true,
  isTraditionEvaluation = false,
} : {
  propertyItem: any;
  /** 是否支持直接提交审批 1:支持 2:不支持，默认值是2 */
  supportDirectApproval: number;
  /** 是否机会点表单，加盟商、踩点记录时false */
  isChancepoint: boolean;
  /** 是否传统的点位评估编辑页（不包含一鸣等租户走机会点点位评估审批流的场景），是的话item的显示、禁用，参照的是机会点拓店模板、非流程设置 */
  isTraditionEvaluation: boolean;
}) => {
  const isShow = isPropertyItemShow({
    propertyItem,
    supportDirectApproval,
    isChancepoint,
    isTraditionEvaluation,
  });
  const isDisable = isPropertyItemDisable({
    propertyItem,
    supportDirectApproval,
    isChancepoint,
    isTraditionEvaluation,
  });
  return isShow && !isDisable;
};

/** 动态表单项字段-店铺周边竞品 */
export const contendInfoFields: Array<any> = [
  { label: '竞品名称', name: 'name', type: 'text', required: false, maxlength: 30 },
  { label: '实际面积', name: 'area', type: 'number', suffix: '平方米', maxValue: 999.99, floatLength: 2, required: false, },
  { label: '月租金', name: 'annualRent', type: 'number', suffix: '元', maxValue: 99999999.99, floatLength: 2, required: false, },
  // https://confluence.lanhanba.com/pages/viewpage.action?pageId=132275109
  // { label: '客单价（线上）', name: 'onlineCustomerPrice', type: 'number', suffix: '元', maxValue: 999.99, floatLength: 2, required: false, },
  // { label: '客单价（线下）', name: 'offlineCustomerPrice', type: 'number', suffix: '元', maxValue: 999.99, floatLength: 2, required: false, },
  { label: '在职员工数', name: 'employeeCount', type: 'number', suffix: '人', maxValue: 999, floatLength: 0, required: false, },
  { label: '日均预估', name: 'dayEstimate', type: 'number', suffix: '元', maxValue: 999999999.99, floatLength: 2, required: false, placeholder: '请输入竞品日均销售额（含外卖）' },
  { label: '外卖月总单量', name: 'dailyTakeOutNumber', type: 'number', maxValue: 99999, floatLength: 0, required: false, placeholder: '美团+饿了么外卖月单量' },
];

/** 动态表单项字段-竞争条件 */
export const competitiveConditionsFields: Array<any> = [
  { label: '品牌名称', name: 'name', type: 'text', required: true, maxlength: 10 },
  { label: '面积', name: 'area', type: 'number', suffix: 'm²', maxValue: 999.99, floatLength: 2, required: true, },
  { label: '竞品商务条件', name: 'competitiveBusinessConditions', type: 'textarea', required: true, maxlength: 100 },
  { label: '其他', name: 'other', type: 'textarea', required: false, maxlength: 100 },
];

// 对标品牌字段（表单组件、及详情组件中都要使用到）
export const benchBrandsFields: Array<any> = [
  { label: '品牌名称', name: 'name', type: 'text', required: true, maxlength: 30 },
  { label: '预估业绩', name: 'estimatedSales', type: 'number', suffix: '元/月', maxValue: 99999999.99, minValue: 0, floatLength: 2, required: true, },
  { label: '租金', name: 'rent', type: 'number', suffix: '元/月', maxValue: 99999999.99, minValue: 0, floatLength: 2, required: true, },
  { label: '客单价', name: 'customerPrice', type: 'number', suffix: '元', maxValue: 999999999.99, minValue: 0, floatLength: 2, required: true, },
];

// -----回本周期 start-----
// 实用面积的档位
export enum AreaValLevel {
  Level1 = 'level1',
  Level2 = 'level2',
  Level3 = 'level3',
  Level4 = 'level4',
  Level5 = 'level5',
}
// 不同实用面积档位对应的字段值
export const areaMapVal: any = {
  level1: {
    renovationCosts: 47500, // 装修费
    equipmentCosts: 92500, // 设备费
    selfBuyingCosts: 17500, // 自购费用
    franchiseFee: 80000, // 加盟费
  },
  level2: {
    renovationCosts: 52500, // 装修费
    equipmentCosts: 100000, // 设备费
    selfBuyingCosts: 17500, // 自购费用
    franchiseFee: 80000, // 加盟费
  },
  level3: {
    renovationCosts: 77500, // 装修费
    equipmentCosts: 100000, // 设备费
    selfBuyingCosts: 17500, // 自购费用
    franchiseFee: 80000, // 加盟费
  },
  level4: {
    renovationCosts: 82500, // 装修费
    equipmentCosts: 100000, // 设备费
    selfBuyingCosts: 22500, // 自购费用
    franchiseFee: 80000, // 加盟费
  },
  level5: {
    renovationCosts: 112500, // 装修费
    equipmentCosts: 100000, // 设备费
    selfBuyingCosts: 27500, // 自购费用
    franchiseFee: 80000, // 加盟费
  }
};
// -----回本周期 end-----

// https://confluence.lanhanba.com/pages/viewpage.action?pageId=132271748
// ------ 益禾堂预估销售额2.0 start ------
export const estimateSalesUpLimitYHT = { // 营业额预估上限
  label: '营业额预估上限',
  keyValue: 'estimateSalesUpLimitYHT',
};
export const insiderEstimateSalesYHT = { // 个人预估营业额
  label: '个人预估营业额',
  keyValue: 'insiderEstimateSalesYHT',
};
export const meituanCompetitorAverageYHT = { // 美团竞品均值
  label: '美团竞品均值',
  keyValue: 'meituanCompetitorAverageYHT',
};
export const meituanCompetitorMaxYHT = { // 美团竞品高值
  label: '美团竞品高值',
  keyValue: 'meituanCompetitorMaxYHT',
};
export const estimateSalesUpLimitSelectYHT = { // 营业额预估上限
  label: '营业额预估挑战值',
  keyValue: 'estimateSalesUpLimitSelectYHT',
};
export const estimateSalesReasonableSelectYHT = { // 营业额预估合理值
  label: '营业额预估合理值结论',
  keyValue: 'estimateSalesReasonableSelectYHT'
};
export const estimateSalesSafetySelectYHT = { // 营业额预估保底值
  label: '营业额预估保底值',
  keyValue: 'estimateSalesSafetySelectYHT'
};

export const echoEstimateSalesComYHT = (textValue: any, form: any) => {
  form.setFieldValue(estimateSalesUpLimitYHT.keyValue, textValue?.[estimateSalesUpLimitYHT.keyValue]);
  form.setFieldValue(insiderEstimateSalesYHT.keyValue, textValue?.[insiderEstimateSalesYHT?.keyValue]);
  form.setFieldValue(meituanCompetitorAverageYHT.keyValue, textValue?.[meituanCompetitorAverageYHT.keyValue]);
  form.setFieldValue(meituanCompetitorMaxYHT.keyValue, textValue?.[meituanCompetitorMaxYHT.keyValue]);
  form.setFieldValue(estimateSalesUpLimitSelectYHT.keyValue, textValue?.[estimateSalesUpLimitSelectYHT.keyValue]);
  form.setFieldValue(estimateSalesReasonableSelectYHT.keyValue, textValue?.[estimateSalesReasonableSelectYHT.keyValue]);
  form.setFieldValue(estimateSalesSafetySelectYHT.keyValue, textValue?.[estimateSalesSafetySelectYHT.keyValue]);
};

/**
 * 客流计数字段回显
 */
export const echoPassengerFlowCom = (textValue: any, form: any) => {
  form.setFieldsValue({
    [PASSENGER_FLOW_MODE.key]: textValue?.[PASSENGER_FLOW_MODE.key],
    [PASSENGER_FLOW_SPOTS.key]: textValue?.[PASSENGER_FLOW_SPOTS.key],
    [PASSENGER_FLOW_WEEKDAY.key]: textValue?.[PASSENGER_FLOW_WEEKDAY.key],
    [PASSENGER_FLOW_WEEKEND.key]: textValue?.[PASSENGER_FLOW_WEEKEND.key]
  });
};

export const echoEstimateSalesConclusionComYHT = (textValue: any, form: any) => {
  form.setFieldValue(estimateSalesReasonableSelectYHT.keyValue, textValue?.[estimateSalesReasonableSelectYHT.keyValue]);
};
// 预估营业额挑战值组件因配置的原因，在表单中不渲染时，关联的定制组件也要往组件里塞对应的数据
export const readInDataWhenTargetHide = (allComps: any, optionItem: any) => {
  const targetCom = allComps.find((item) => item.controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value);
  if (!targetCom) return;
  // 益禾堂一定会配置审批流
  const { access, isShow } = targetCom;
  // access 1：仅可见  2：可写 3:不可见 null:历史数据的不可见
  if (isShow && (access === 1 || access === 2)) return;
  const oldVal = parseValueCatch(targetCom) || {};
  const options = oldVal?.options || [];
  if (isArray(options)) {
    // 通过唯一标识符去重（新值在前，重复时保留新值）
    const removeRepetVal = concatObjArray([optionItem], options, 'value');
    // 清空的时候过滤
    const targetVal = removeRepetVal?.filter((item) => isNotEmpty(item.countValue));
    targetCom.textValue = JSON.stringify({
      estimateSalesReasonableSelectYHT: oldVal[estimateSalesReasonableSelectYHT.keyValue] || '',
      options: targetVal
    });
  }
};

// ------ 益禾堂预估销售额2.0   end ------

// https://confluence.lanhanba.com/pages/viewpage.action?pageId=132273933
// ------ 益禾堂定制字段-人口流量 start ------
// export const populationFlowYHTOfCategory = 'population_flow_yht_bear_of_gathering_point_category';
export const populationFlowYHTOfSelectOptions = [
  {
    value: ['社区型', '办公型', '复合型'],
    label: '固定量',
    options: [
      { label: '5万人≤1公里人数', value: 1 },
      { label: '3万人≤1公里<5万人', value: 2 },
    ]
  },
  {
    value: ['文教型'],
    label: '固定量',
    options: [
      { label: '大学校园店在校人数>8000', value: 3 },
      { label: '5000≤在校人数<8000', value: 4 },
      { label: '在校人数＜5000', value: 5 },
    ]
  },
  {
    value: ['商业区'],
    label: '流动量',
    options: [
      { label: '辐射全省', value: 6 },
      { label: '辐射全市', value: 7 },
      { label: '辐射全区', value: 8 },
      { label: '辐射周边3公里', value: 9 },
      { label: '辐射周边社区', value: 10 },
    ]
  },
  {
    value: ['景区型'],
    label: '流动量',
    options: [
      { label: '5A级景区', value: 11 },
      { label: '4A级景区', value: 12 },
      { label: '3A级景区', value: 13 },
      { label: '其他', value: 14 },
    ]
  },
  {
    value: ['交通枢纽型'],
    label: '流动量',
    options: [
      { label: '特等站（省会高铁站）', value: 15 },
      { label: '一等站', value: 16 },
      { label: '二等站', value: 17 },
      { label: '三等站', value: 18 },
      { label: '四等站', value: 19 },
      { label: '机场（一线城市及省会）', value: 20 },
      { label: '其他机场', value: 21 },
    ]
  },
  {
    value: ['其他'],
    label: '', // 取组件配的
    options: [
      { label: '其他', value: 99 },
    ]
  },
];
// ------ 益禾堂定制字段-人口流量 start ------

/**
 * @description 根据控件类型获取搜索类型配置（提供给动态表头）
 */
export const getSearchTypeConfigByControlType = ({ customKey, controlType, options = [], render, extra }: { customKey?: string, controlType: number, options?: any[], render?: any, extra?: any }) => {
  const map = {
    [ControlType.SINGLE_RADIO.value]: { customKey, type: 'formSelect', options, extra: { controlType, ...extra || {} } },
    [ControlType.CHECK_BOX.value]: { customKey, type: 'formSelect', options, multiple: true, extra: { controlType, ...extra || {} } },
    [ControlType.INPUT.value]: { customKey, type: 'formInput', extra: { controlType, ...extra || {} } },
    [ControlType.TEXT_AREA.value]: { customKey, type: 'formInput', extra: { controlType, ...extra || {} } },
    [ControlType.INPUT_NUMBER.value]: { customKey, type: 'formRangeInput', extra: { controlType, ...extra || {} } },
    [ControlType.TIME.value]: { customKey, type: 'formRangePicker', extra: { controlType, ...extra || {} } },
    [ControlType.ADDRESS.value]: { customKey, type: 'formInput', extra: { controlType, ...extra || {} } },
    [ControlType.AREA.value]: { customKey, type: 'formProvinceTree', multiple: true, extra: { controlType, ...extra || {} } },
    [ControlType.CUSTOM_AREA.value]: { customKey, type: 'formProvinceTree', multiple: true, extra: { controlType, ...extra || {} } },
  };
  const otherConfig = map[ControlType.INPUT.value] || {};
  const data = map[controlType] || otherConfig;
  if (render) {
    return { ...data, type: 'custom', render };
  }
  return data;
};

/**
 * @description 根据控件类型获取搜索类型配置（提供给静态表头）
 */
export const getSearchTypeConfigByStaticType = ({ customKey, type, options = [], render, extra }: { customKey?: string, type: string, options?: any[], render?: any, extra?: any}) => {
  const map = {
    formSelect: { customKey, type: 'formSelect', options, multiple: true, extra: { controlType: ControlType.CHECK_BOX.value, ...extra || {} } },
    formInput: { customKey, type: 'formInput', extra: { controlType: ControlType.INPUT.value, ...extra || {} } },
    formRangeInput: { customKey, type: 'formRangeInput', extra: { controlType: ControlType.INPUT_NUMBER.value, ...extra || {} } },
    formRangePicker: { customKey, type: 'formRangePicker', extra: { controlType: ControlType.TIME.value, ...extra || {} } },
    formProvinceTree: { customKey, type: 'formProvinceTree', dataType: 1, multiple: true, extra: { controlType: ControlType.AREA.value, ...extra || {} } },
    custom: { customKey, type: 'custom', render, extra: { controlType: ControlType.CHECK_BOX.value, ...extra || {} } },
  };
  const otherConfig = map.formInput || {};
  return map[type] || otherConfig;
};

/**
 * 检查筛选项是否是表头自定义搜索项
 */
export const checkIsHeaderCustomSearch = (item: any) => {
  return typeof item === 'object' && isNotEmpty(item?.comType);
};

const getHeaderCustomSearchItemValue = (item: any) => {
  if (item && typeof item === 'object') {
    const value = item.selectedId ?? item.id ?? item.value;
    return isDef(value) ? value : item;
  }
  return item;
};

const normalizeHeaderCustomSearchValue = (value: any) => {
  if (isArray(value)) {
    return value.map(getHeaderCustomSearchItemValue);
  }
  return getHeaderCustomSearchItemValue(value);
};

/**
 * @description 解析表格表头自定义搜索参数
 */
export const parseTableHeaderCutomSearch = (params): any => {
  const otherParams = {};

  const searchList = Object.entries(params || {}).map(([key, item]: any) => {
    if (checkIsHeaderCustomSearch(item)) {
      let value = item.value;

      switch (item.comType) {
        case 'formSelect':
          value = isArray(value) ? (value.length ? value : null) : (isNotEmpty(value) ? [value] : null);
          break;
        case 'formRangeInput':
          value = Array.isArray(value) && value.length ? { min: value?.[0], max: value?.[1] } : null;
          break;
        case 'formRangePicker':
          value = Array.isArray(value) && value.length ? { start: value?.[0], end: value?.[1] } : null;
          break;
        case 'formProvinceTree': {
          const needProvinceIds = isNotEmpty(item.extra?.needProvinceIds) ? item.extra?.needProvinceIds : true;
          const needCityIds = isNotEmpty(item.extra?.needCityIds) ? item.extra?.needCityIds : true;
          const needDistrictIds = isNotEmpty(item.extra?.needDistrictIds) ? item.extra?.needDistrictIds : true;
          value = Array.isArray(value) && value.length ? value.reduce((result, provinceItem: any) => {
            const arr = provinceItem?.value?.split(',');
            if (arr.length === 1) {
              needProvinceIds && result.provinceIds.push(arr[0]);
            } else if (arr.length === 2) {
              needCityIds && result.cityIds.push(arr[1]);
            } else if (arr.length === 3) {
              needDistrictIds && result.districtIds.push(arr[2]);
            }
            return result;
          }, { provinceIds: [], cityIds: [], districtIds: [] }) : null;
          break;
        }
        case 'custom':
          value = normalizeHeaderCustomSearchValue(value);
          break;
        default:
          break;
      }

      return {
        key,
        value: isNotEmpty(value) ? (typeof value === 'string' ? value.trim() : JSON.stringify(value)) : value,
        formType: item.comType,
        controlType: item.extra?.controlType || null,
      };
    }
    otherParams[key] = item;
    return null;
  }).filter(item => item && isNotEmpty(item.value));

  return {
    ...otherParams,
    searchList,
  };
};
